import { injectable, inject } from 'inversify';
import { EventStream, EventStreamSymbol } from '@/app/event-stream/event-stream';
import { SectionDuration, SectionDurationSymbol } from '@/app/statistics/section/section-duration';
import { createEvent } from '@/app/event-stream';
import {
  SectionClosedEvent,
  SectionClosedEventName,
  SectionOpenedEvent,
  SectionOpenedEventName
} from '@/app/statistics/events/navigation-events';
import sectionStoreModule from '@/app/statistics/state/section-state';

@injectable()
export class SectionNavigationService {
  public get currentSectionId(): string|null {
    return sectionStoreModule.currentSectionId;
  }

  public set currentSectionId(value: string|null) {
    sectionStoreModule.setCurrentSectionId(value);
  }

  private get currentSectionTitle(): string|null {
    return sectionStoreModule.currentSectionTitle;
  }

  private set currentSectionTitle(value: string|null) {
    sectionStoreModule.setCurrentSectionTitle(value);
  }

  constructor(
    @inject(EventStreamSymbol)
    private eventStream: EventStream,
    @inject(SectionDurationSymbol)
    private sectionDuration: SectionDuration) {
  }

  public openSection(sectionId: string, title: string): void {
    this.closeSectionIfNecessary();

    this.dispatchOpenedEvent(sectionId, title);
    this.currentSectionId = sectionId;
    this.currentSectionTitle = title;
    this.sectionDuration.start();
  }

  public closeSectionIfNecessary(): void {
    if (this.currentSectionId !== null && this.currentSectionTitle !== null) {
      this.dispatchClosedEvent(this.currentSectionId, this.currentSectionTitle);
      this.currentSectionId = null;
      this.currentSectionTitle = null;
    }
  }

  private dispatchOpenedEvent(sectionId: string, title: string): void {
    const event = createEvent<typeof SectionOpenedEventName, SectionOpenedEvent>(SectionOpenedEventName, {
      sectionId,
      title
    });
    this.eventStream.dispatch(event);
  }

  private dispatchClosedEvent(sectionId: string, title: string): void {
    const event = createEvent<typeof SectionClosedEventName, SectionClosedEvent>(SectionClosedEventName, {
      sectionId,
      title,
      durationSeconds: this.sectionDuration.durationSeconds
    });
    this.eventStream.dispatch(event);
  }
}

export const SectionNavigationServiceSymbol = Symbol.for('SectionNavigationService');
